import React from 'react';
import { useLazyQuery, gql } from '@apollo/client';
import { Box, Button, Paper, CircularProgress, Stack, Typography } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const EXPLAIN_WORD = gql`
  query ExplainWordOrPhrase($text: String!) {
    explainWordOrPhrase(text: $text)
  }
`;

interface HelpExplanationProps {
  word: string;
}

export const HelpExplanation: React.FC<HelpExplanationProps> = ({ word }) => {
  const [isExplainOpen, setIsExplainOpen] = React.useState<boolean>(false);
  const [explainWord, { loading: loadingExplain, data: explainData, error: explainError }] = useLazyQuery(EXPLAIN_WORD);

  React.useEffect(() => {
    setIsExplainOpen(false);
  }, [word]);

  const handleToggleExplain = () => {
    const nextState = !isExplainOpen;
    setIsExplainOpen(nextState);
    if (nextState) {
      explainWord({ variables: { text: word } });
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Button
        size="small"
        color={isExplainOpen ? 'error' : 'secondary'}
        variant="outlined"
        onClick={handleToggleExplain}
        startIcon={<HelpOutlineIcon />}
        sx={{ textTransform: 'none', borderRadius: 2 }}
      >
        {isExplainOpen ? 'Hide AI Explanation' : 'Explain word/phrase'}
      </Button>

      {isExplainOpen && (
        <Paper
          elevation={0}
          sx={{
            mt: 2,
            p: 2,
            textAlign: 'left',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'action.hover',
          }}
        >
          {loadingExplain ? (
            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center">
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Sven is writing an explanation...
              </Typography>
            </Stack>
          ) : explainError ? (
            <Typography variant="body2" color="error">
              Could not generate explanation: {explainError.message}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
              {explainData?.explainWordOrPhrase || 'No explanation returned.'}
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
};
